import ReactDOM from "react-dom/client";
import { Redirect, Route, Switch } from "wouter";
import LoginPage from "./app/auth/(login)/login/page";
import LoginLayout from "./app/auth/(login)/layout";
import ForgotPasswordPage from "./app/auth/(login)/forgot-password/page";
import ResetEmailSentPage from "./app/auth/(login)/reset-email-sent/page";
import ResetPasswordPage from "./app/auth/(login)/reset-password/page";
import PaymentInformationPage from "./app/auth/(signup)/payment-information/page";
import SignupPage from "./app/auth/(signup)/signup/page";
import ConnectSellerApiPage from "./app/connect/seller-api/page";
import SellerApiConnectSucceedPage from "./app/connect/seller-api/succeed/page";
import AssistantPage from "./app/auth/(signup)/assistants/page";
import AdminLayout from "./app/admin/AdminLayout";
import AdminUsersPage from "./app/admin/AdminUsersPage";
import AdminDashboardPage from "./app/admin/AdminDashboardPage"
import DashboardLayout from "./app/dashboard/DashboardLayout/DashboardLayout";
import SettingsLayout from "./app/settings/layout";
import AnalyticsLayout from "./app/analytics/layout";
import AccountLayout from "./app/account/layout";
import ProfitAndLossPage from "./app/analytics/profit-and-loss/page";
import ProfitCategoryPage from "./app/analytics/profit-by-category/page";
import ProfitBySkuPage from "./app/analytics/profit-by-sku/page";
import ProfitBySkuIdPage from "./app/analytics/profit-by-sku/[id]/page";
import ProfitByAsinPage from "./app/analytics/profit-by-asin/page";
import ProfitByAsinIdPage from "./app/analytics/profit-by-asin/[id]/page";
import ProfitByCategoryPage from "./app/analytics/profit-by-category/[category]/page";
import Dashboard from "./app/dashboard/DashboardPage/DashboardPage";
import InventoryPage from "./app/inventory/InventoryPage/InventoryPage";
import SettingsDetailsPage from "./app/settings/details/page";
import SettingsPrintingPage from "./app/settings/printing/page";
import SettingsProfilePage from "./app/settings/profile/page";
import SettingsTeamPage from "./app/settings/team/page";
import VariableSKUPage from "./app/settings/variable_sku/page";
import FbaCreatePage from "./app/fba/FbaCreatePage/FbaCreatePage";
import FbaShipmentFlow from "./app/fba/FbaShipmentFlow";
import FbaListingsCreatePage from "./app/fba-listings/FbaListingsCreatePage";
import FbmListingsCreatePage from "./app/fbm-listings/FbmListingsCreatePage/FbmListingsCreatePage";
import ExternalShipmentPage from "./app/shipments/ExternalShipmentPage/ExternalShipmentPage";
import ShipmentsPage from "./app/shipments/ShipmentsPage/ShipmentsPage";
import RootLayout from "./components/Rootlayout/RootLayout";
import ProfitOverviewPage from "./app/analytics/profit-overview/page";
import AdminChartsPage from "./app/admin/AdminChartsPage/AdminChartsPage";
import AdminErrorsPage from "./app/admin/AdminErrorsPage";
import IntegrationPage from "./app/settings/integration/page";
import MorePage from "./app/more/page";
import { ProductResearchPage } from "./app/product-research/ProductResearchPage/ProductResearchPage";
import BillingRecurlyPage from "./app/settings/billing-recurly/page";
import PageNotFound from "./app/PageNotFound";
import NewVerifyEmailPage from "./app/auth/(login)/new-verify-email/new-verify-email.page";
import { DraggingState, oneTimeDragNDropInit } from "./app/fba/FbaShipmentFlow/FbaShipmentFlow/DragNDrop";

declare global {
  interface Window {
    LogRocket: any;
    Intercom: any;
  }
}

declare const GIT_SHORTHASH: string; // git-shorthash of built application
declare const BUILT_AT: string; // ISO-8601 timestamp of the time this build was generated 

const globalDraggingState: DraggingState = {isDragging: false};
oneTimeDragNDropInit(globalDraggingState);

const App = () => (
  <RootLayout>
    <Switch>
      <Route path="/auth/forgot-password">
        <LoginLayout>
          <ForgotPasswordPage />
        </LoginLayout>
      </Route>
      <Route path="/auth/login">
        <LoginLayout>
          <LoginPage />
        </LoginLayout>
      </Route>
      <Route path="/auth/reset-email-sent">
        <LoginLayout>
          <ResetEmailSentPage />
        </LoginLayout>
      </Route>
      <Route path="/auth/reset-password">
        <LoginLayout>
          <ResetPasswordPage />
        </LoginLayout>
      </Route>
      <Route path="/auth/verify-email">
        <LoginLayout>
          <NewVerifyEmailPage />
        </LoginLayout>
      </Route>

      <Route path="/auth/payment-information">
        <PaymentInformationPage />
      </Route>

      <Route path="/auth/signup/assistants">
        <AssistantPage />
      </Route>

      <Route path="/auth/signup">
        <SignupPage />
      </Route>

      <Route path="/connect/seller-api" component={ConnectSellerApiPage} />
      <Route path="/connect/seller-api/succeed" component={SellerApiConnectSucceedPage} />

      <Route path="/admin" nest>
        <AdminLayout>
          <Switch>
            <Route path="/dashboard" component={AdminDashboardPage} />
            <Route path="/users" component={AdminUsersPage} />
            <Route path="/charts" component={AdminChartsPage} />
            <Route path="/errors" component={AdminErrorsPage} />
          </Switch>
        </AdminLayout>
      </Route>

      <Route>
        <DashboardLayout>
          <Switch>
            <Route path="/analytics" nest>
              <AnalyticsLayout>
                <Switch>
                  <Route path="/profit-overview" component={ProfitOverviewPage} />
                  <Route path="/profit-loss" component={ProfitAndLossPage} />
                  <Route path="/profit-by-category" component={ProfitCategoryPage} />
                  <Route path="/profit-by-category/:category" component={ProfitByCategoryPage} />
                  <Route path="/profit-by-sku" component={ProfitBySkuPage} />
                  <Route path="/profit-by-sku/:id" component={ProfitBySkuIdPage} />
                  <Route path="/profit-by-asin" component={ProfitByAsinPage} />
                  <Route path="/profit-by-asin/:id" component={ProfitByAsinIdPage} />
                </Switch>
              </AnalyticsLayout>
            </Route>
            <Route path="/" component={Dashboard} />
            <Route path="/inventory" component={InventoryPage} />
            <Route path="/settings" nest>
              <SettingsLayout>
                <Switch>
                  <Route path="/billing" component={BillingRecurlyPage} />
                  <Route path="/details" component={SettingsDetailsPage} />
                  <Route path="/printing" component={SettingsPrintingPage} />
                  <Route path="/profile" component={SettingsProfilePage} />
                  <Route path="/team" component={SettingsTeamPage} />
                  <Route path="/variable_sku" component={VariableSKUPage} />
                  <Route path="/integration" component={IntegrationPage} />
                </Switch>
              </SettingsLayout>
            </Route>
            <Route path="/account" nest>
              <AccountLayout>
                <Switch>
                  <Route path="/billing" component={BillingRecurlyPage} />
                  <Route path="/profile" component={SettingsProfilePage} />
                  <Route path="/team" component={SettingsTeamPage} />
                  <Route path="/billing-recurly" component={BillingRecurlyPage} />
                </Switch>
              </AccountLayout>
            </Route>
            <Route path="/more" component={MorePage} />
            <Route path="/fba/shipments/create" component={FbaCreatePage} />
            <Route path="/fba/shipments/:batchId">
              <FbaShipmentFlow draggingState={globalDraggingState} />
            </Route>
            <Route path="/fba/shipments/:batchId/*">
              {(params: {batchId: string}) => <Redirect to={`/fba/shipments/${params.batchId}`} replace />}
            </Route>

            <Route path="/fba/listings/create">
              <FbaListingsCreatePage />
            </Route>

            <Route path="/fbm/listings/create">
              <FbmListingsCreatePage />
            </Route>

            <Route path="/shipments/external/:shipmentId" component={ExternalShipmentPage} />
            <Route path="/shipments" component={ShipmentsPage} />

            <Route path="/product-research">
              <ProductResearchPage/>
            </Route>

            {/* important to place at the end so it is only matched if no other routes match */}
            <Route component={PageNotFound} />
          </Switch>
        </DashboardLayout>
      </Route>
    </Switch>
  </RootLayout>
);

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(<App />);

console.info(`Boxem version: ${GIT_SHORTHASH}, built at: ${new Date(BUILT_AT).toLocaleString()}`);
